import { Box, Button, Collapse, darken, Divider, lighten, MenuItem, MenuList, Popover, styled, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState, useTransition } from 'react'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { routeName } from '../../routes/routeName'
import { CONTENT_TYPE, FrontApi, globalSearchType, USER_TYPE } from '../../api/front-api/front.api'
import { colors } from '../../constants/colors'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplyIcon from '@mui/icons-material/Reply';
import clsx from 'clsx'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { icons } from '../../assets/icons/icons'
import { AuthApi } from '../../api/auth/auth.api'
import { alertError, alertSuccess } from '../alert/swal'
import { setLoading, unsetLoading } from '../../store/loading/loading.slice'
import { debouncedApiCall, getToken, getTokenMember, logout } from '../../utils/app.utils'
import StateComponents from '../state-component/StateComponents'
import Hashids from 'hashids'
import ChangeLanguage from '../i18n-dropdown/ChangeLanguage'
import { useTranslation } from 'react-i18next'
import { clearProfile } from '../../store/profile/profile.slice'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';

type Props = {}

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor: lighten(theme.palette.primary.light, 0.85),
    ...theme.applyStyles('dark', {
        backgroundColor: darken(theme.palette.primary.main, 0.8),
    }),
}));

const GroupItems = styled('ul')({
    padding: 0,
});

const HeaderWebsite = ({ }: Props) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();
    const currentPath = location.pathname;
    const mode = theme.palette.mode;
    const token = getToken();
    const token_member = getTokenMember();
    const hash_ids = new Hashids(process.env.REACT_APP_SECRET_SALT, 50);


    const profile = useSelector((state: RootState) => state.profile.profile) as any;
    const base_url = useSelector((state: RootState) => state.api.image_api);

    const user_type = profile?.user_type as USER_TYPE;
    const is_employee = user_type === USER_TYPE.EMPLOYEE;
    const is_student = user_type === USER_TYPE.STUDENT;

    const [selectedValue, setSelectedValue] = useState<any>(null);
    const [isPending, startTransition] = useTransition();
    const [menuData, setMenuData] = useState<any[]>([]);
    const [openSubStates, setOpenSubStates] = useState<{ [key: string]: boolean }>({});
    const [subMenuAnchors, setSubMenuAnchors] = useState<{ [key: string]: HTMLElement | null }>({});
    const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>({});
    const [headerData, setHeaderData] = useState<any>(null);
    const [keyword, setKeyword] = useState<string>("");
    const [dataSearch, setDataSearch] = useState<any[]>([]);

    const handleSubItemClick = (subItem: any) => {
        const hasNestedSub = subItem.subMenu && subItem.subMenu.length > 0;
        if (hasNestedSub) {
            setOpenSubStates(prev => ({
                ...prev,
                [subItem.code]: !prev[subItem.code],
            }));
            return;
        }
        let targetPath = subItem.path;
        if (subItem.path) {
            if (subItem.path.startsWith('CT') && !subItem.path.startsWith('ctdw')) {
                targetPath = `/content/${subItem.path}`;
            } else if (subItem.path.startsWith('ctdw')) {
                targetPath = `/content-daily-warning/${subItem.path}`;
            }
        }

        startTransition(() => {
            navigate(targetPath);
        });

        setOpenSubMenus({});
        setSubMenuAnchors({});
        setOpenSubStates({});
    };

    const redirect_url = (user_type: string) => {
        switch (user_type) {
            case "employee": {
                navigate(routeName.cms.ListOfApplicants);
                break;
            }
            case "advisor": {
                navigate(routeName.cms.ListOfApplicants);
                break;
            }
            case "student": {
                navigate(routeName.student.chooseAdvisor);
                break;
            }
            case "employee": {
                navigate(routeName.cms.ListOfApplicants);
                break;
            }
        }
    }


    const handleSubMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        menuCode: string
    ) => {
        event.stopPropagation();
        setSubMenuAnchors(prev => ({
            ...prev,
            [menuCode]: event.currentTarget
        }));
        setOpenSubMenus(prev => ({
            ...prev,
            [menuCode]: true
        }));
    };


    const handleNavItemClick = (list: any, i: number, event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const hasSubMenu = list.subMenu && list.subMenu.length > 0;
        if (hasSubMenu) {
            handleSubMenuOpen(event, list.code);
        } else {
            let targetPath = list.path;
            if (list.path) {
                if (list.path.startsWith('CT')) {
                    targetPath = `/content/${list.path}`;
                } else if (list.path === 'main') {
                    targetPath = '/'
                }
            }
            startTransition(() => {
                navigate(targetPath);
            });
        }
    };

    const mapMenuData = (menus: any[]): any[] => {
        return menus.map(menu => ({
            name: menu.menu_name,
            icon: "",
            code: menu.menu_name.toLowerCase().replace(/\s+/g, '_'),
            path: menu.menu_link,
            is_not_show: menu?.is_not_show,
            permit: true,
            action: true,
            subMenu: menu.children?.length ? mapMenuData(menu.children) : []
        }));
    };

    const handleSubMenuClose = (menuCode: string) => {
        setSubMenuAnchors(prev => ({
            ...prev,
            [menuCode]: null
        }));
        setOpenSubMenus(prev => ({
            ...prev,
            [menuCode]: false
        }));
    };

    const renderSubMenu = (subMenu: any[], level = 1) => {
        return (
            <MenuList sx={{ pl: level * 2 }}>
                {subMenu?.filter((x) => !x?.is_not_show).map((subItem: any) => {
                    const hasNestedSub = subItem.subMenu && subItem.subMenu.length > 0;
                    const isActive = currentPath === subItem.path;
                    const isOpen = openSubStates[subItem.code] || false;

                    return (
                        <Box key={subItem.code}>
                            <MenuItem
                                onClick={() => handleSubItemClick(subItem)}
                                sx={{
                                    borderRadius: '4px',
                                    margin: '2px 8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    pl: level * 2,
                                    cursor: 'pointer'
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: isActive ? colors.themeMainColor : mode === 'dark' ? '#fff' : '#333',
                                        fontSize: '14px',
                                    }}
                                >
                                    {level > 1 && <ReplyIcon sx={{ rotate: "180deg", mr: 1 }} />}
                                    {subItem.name}
                                </Typography>

                                {hasNestedSub && (
                                    <ExpandMoreIcon
                                        sx={{
                                            fontSize: 16,
                                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                )}
                            </MenuItem>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Box sx={{ pl: 1 }}>
                                    {renderSubMenu(subItem.subMenu, level + 1)}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </MenuList>
        );
    };

    const renderMenu = (menuData: any[]) => {
        return (
            <>
                {menuData?.filter((x) => !x?.is_not_show).map((list, i) => {
                    const hasSubMenu = list.subMenu && list.subMenu.length > 0;
                    const isSubMenuOpen = openSubMenus[list.code] || false;
                    const isSubMenuActive = hasSubMenu && list.subMenu.some((subItem: any) => subItem.path === currentPath);

                    return (
                        <React.Fragment key={list.code}>
                            <Box
                                component={'span'}
                                sx={(theme) => ({
                                    pointerEvents: isPending ? 'none' : 'auto',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    position: 'relative'
                                })}
                                onClick={(event) => handleNavItemClick(list, i, event)}
                                className={clsx('pt-2 mt-4 cursor-pointer hover:opacity-55 duration-300 transform hover:translate-x-2 px-4 transition-all')}
                            >
                                <Typography sx={(theme) => ({
                                    color: (currentPath === list.path || isSubMenuActive) ? colors.themeMainColorThree : "#3F3939"
                                })}>
                                    {list.name}
                                </Typography>
                                {hasSubMenu && (
                                    <ExpandMoreIcon
                                        sx={(theme) => ({
                                            color: (currentPath === list.path || isSubMenuActive) ? 'white' : "#3F3939",
                                            transform: isSubMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease'
                                        })}
                                    />
                                )}
                            </Box>

                            {/* Sub-menu Popover */}
                            {hasSubMenu && (
                                <Popover
                                    container={document.body}
                                    open={isSubMenuOpen}
                                    anchorEl={subMenuAnchors[list.code]}
                                    onClose={() => handleSubMenuClose(list.code)}
                                    disableScrollLock
                                    disableAutoFocus
                                    disableEnforceFocus
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                    sx={{
                                        zIndex: 1300,
                                        '& .MuiPaper-root': {
                                            backgroundColor: mode === 'dark' ? '#333' : 'white',
                                            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
                                            borderRadius: '8px',
                                            mt: 1,
                                            minWidth: '200px'
                                        },
                                    }}
                                    marginThreshold={0}
                                    PaperProps={{
                                        style: {
                                            maxHeight: '400px',
                                            overflowY: 'auto'
                                        }
                                    }}
                                >
                                    {renderSubMenu(list?.subMenu || [])}
                                </Popover>
                            )}
                        </React.Fragment>
                    )
                })}
            </>
        )
    }

    const handleGetHeaderData = async () => {
        await FrontApi.header().then((res) => {
            if (res.status < 300) {
                const data = res.data?.data || [];
                setHeaderData(data);
                // console.log(data, 'data')
            }
        }).catch((err) => {
            console.log(err)
        })
    }
    const handleGetGlobalSearch = useCallback(async () => {
        let query: globalSearchType = {
        }
        if (keyword) query.keyword = keyword
        await FrontApi.globalSearch(query).then((res) => {
            if (res.status < 300) {
                const response = res.data?.data || [];
                setDataSearch(response);
            }
        }).catch((err) => {
            console.log(err)
        })
    }, [keyword])
    const handleLogout = async () => {
        dispatch(setLoading());
        // ตัดสินฝั่งก่อน clear localStorage: ใช้ profile ถ้ามี ไม่งั้น fallback ที่ชนิด token
        // กันกรณี profile เป็น null แล้วเด้ง member ไปหน้า /login ผิดฝั่ง
        const is_member_session = user_type === USER_TYPE.MEMBER || (!!getTokenMember() && !getToken());
        await AuthApi.logout().then((res) => {
            if (res.status < 300) {
                logout(true);
                dispatch(clearProfile());
                if (is_member_session) {
                    navigate(routeName.authentication.login_member);
                } else {
                    navigate(routeName.authentication.login);
                }
                alertSuccess(t("HOME_PAGE_LOG_OUT"), t("HOME_PAGE_LOG_OUT_SUCCESSFULLY"));

            }
        }).catch((err) => {
            console.log(err);
            alertError(t("HOME_PAGE_LOG_OUT"), t("HOME_PAGE_LOGOUT_FAILED"))
        }).finally(() => {
            dispatch(unsetLoading());
        })
    }

    const handleGetMenuHeader = async () => {
        await FrontApi.menuHeader().then((res) => {
            if (res.status < 300) {
                const transformed = mapMenuData(res.data?.data || []);
                setMenuData(transformed);
            }
        }).catch((err) => {
            console.log(err)
        })
    }
    // console.log(profile, 'profile')

    useEffect(() => {
        handleGetHeaderData();
        handleGetMenuHeader();
    }, [])

    useEffect(() => {
        handleGetGlobalSearch();
    }, [])

    return (
        <Box component={"header"} className="w-full flex flex-col">
            <Box className="w-full gap-4 px-4 md:px-0 m-auto md:max-w-[1136px] xl:max-w-[1440px] flex flex-col md:flex-row justify-between">
                <Box className="flex p-1 flex-col md:flex-row justify-start items-center gap-2">
                    <span className='flex justify-start items-center gap-2'>
                        <EmailOutlinedIcon sx={{ color: headerData?.header_name_color || "#4A4A4A" }} />
                        <Typography sx={{ color: headerData?.header_name_color || "#4A4A4A" }}>{headerData?.header_name || "-"}</Typography>
                    </span>
                    <span className='flex justify-start gap-2 items-center'>
                        <FmdGoodOutlinedIcon sx={{ color: headerData?.header_name_desc_color || "#4A4A4A" }} />
                        <Tooltip title={headerData?.header_name_desc} arrow >
                            <Typography className="line-clamp-1" sx={{ color: headerData?.header_name_desc_color || "#4A4A4A" }}>{headerData?.header_name_desc || "-"}</Typography>
                        </Tooltip>
                    </span>
                </Box>
                <Box className="flex flex-wrap gap-4">
                    {token_member ? (
                        <Box className="flex justify-end items-center gap-4">
                            <Tooltip title={`${t("HOME_PAGE_MEMBER")} :  ${profile?.first_name || profile?.member_name || "-"} ${profile?.last_name || profile?.member_last_name || "-"}`} arrow>
                                <Typography className="line-clamp-1" sx={{ fontWeight: 800, fontSize: 20 }}>
                                    {t("HOME_PAGE_MEMBER")} :  {profile?.first_name || profile?.member_name || profile?.student_frist_name || "-"} {profile?.last_name || profile?.member_last_name || profile?.student_last_name || "-"}
                                </Typography>
                            </Tooltip>
                            <Button
                                color='error'
                                sx={{ height: "40px", borderRadius: "0" }}
                                variant='contained' onClick={handleLogout}>
                                {t("HOME_PAGE_LOG_OUT")}
                            </Button>
                        </Box>
                    ) : token ?
                        <>
                            <Box className="flex justify-end items-center gap-4">
                                <Tooltip title={`${t("HOME_PAGE_MEMBER")} :  ${profile?.first_name || profile?.member_name || "-"} ${profile?.last_name || profile?.member_last_name || "-"}`} arrow>
                                    <Typography className="line-clamp-1" sx={{ fontWeight: 800, fontSize: 20 }}>
                                        {t("HOME_PAGE_MEMBER")} :  {profile?.first_name || profile?.member_name || profile?.student_frist_name || "-"} {profile?.last_name || profile?.member_last_name || profile?.student_last_name || "-"}
                                    </Typography>
                                </Tooltip>
                                {is_employee && (
                                    <Button color='success' variant='contained' sx={{ height: "40px", borderRadius: "0" }} onClick={() => navigate(routeName.admission.list_applicants)}>
                                        {t("ระบบบริหารจัดการ")}
                                    </Button>
                                )}
                                {is_student && (
                                    <Button color='success' variant='contained' sx={{ height: "40px", borderRadius: "0" }} onClick={() => navigate(routeName.student.chooseAdvisor)}>
                                        {t("ระบบบริหารจัดการ")}
                                    </Button>
                                )}
                                <Button color='error' sx={{ height: "40px", borderRadius: "0" }} variant='contained' onClick={handleLogout}>{t("HOME_PAGE_LOG_OUT")}</Button>
                            </Box>
                        </> :
                        <>
                            <Button sx={{ height: "40px", borderRadius: "0" }} onClick={() => navigate(routeName.authentication.login_member)}>
                                {t("HOME_PAGE_LOG_IN")}
                            </Button>
                            <Button sx={{ height: "40px", borderRadius: "0" }} onClick={() => (token && profile) ? redirect_url(profile?.user_type!) : navigate(routeName.authentication.login)}>
                                {t("HOME_PAGE_STUDENT/STAFF_LOGIN")}
                            </Button>
                            <Button onClick={() => {
                                navigate({
                                    pathname: routeName.authentication.login_member, search: createSearchParams({
                                        signup: 'SignUp'
                                    }).toString()
                                })
                            }} sx={{ height: "40px", borderRadius: "0" }} variant='contained'>{t("HOME_PAGE_CREATE_A_USER_ACCOUNT")}</Button>
                        </>}
                </Box>
            </Box>
            <Divider />
            <Box className="w-full py-10 px-4 md:px-0 m-auto md:max-w-[1136px] xl:max-w-[1440px]">
                <Box className="flex w-full flex-col xl:flex-row justify-center items-center gap-6">
                    <Box onClick={() => navigate(routeName.main.home)} className="w-full cursor-pointer max-w-fit">
                        {headerData?.header_file ?
                            <img width={160} height={64} src={`${base_url}/${headerData?.header_file?.media_file_path}${headerData?.header_file?.media_file_name}`} alt="head-icon" />
                            : null}
                    </Box>
                    <Box className="w-fit flex justify-center items-center flex-wrap">
                        {renderMenu(menuData)}
                    </Box>
                    <Box className="w-full max-w-fit flex justify-center items-center">
                        <StateComponents
                            sxContainer={() => ({ width: '100%', minWidth: 300 })}
                            typeProps={[
                                {
                                    seq: 1,
                                    type: "AUTO-COMPLETE",
                                    gridProps: {
                                        xs: 12
                                    },
                                    fieldProps: {
                                        labelId: "211",
                                        options: dataSearch || [],
                                        getOptionLabel: (op: any) => typeof op === "string" ? op : op?.content_name || "",
                                        groupBy: (op: any) => op?.content_type || "",
                                        renderGroup: (params: any) => {
                                            return (
                                                <Box component={'li'} key={params.key}>
                                                    <GroupHeader >{params.group}</GroupHeader>
                                                    <GroupItems>{params.children}</GroupItems>
                                                </Box>
                                            )
                                        },
                                        value: selectedValue,
                                        onInputChange: (_: any, val: any) => debouncedApiCall({ searchItem: val, setItem: setKeyword }),
                                        startAdornment: <span className='mr-2'>
                                            <icons.SearchNormal />
                                        </span>,
                                        placeholder: t("HOME_PAGE_SEARCH"),
                                        onChange: (_, val) => {
                                            if (val) {
                                                setSelectedValue(val);
                                                const content_no: string = val?.content_no || "";
                                                const id = hash_ids.encode(val?.id_content) || "";
                                                if (val?.content_type === CONTENT_TYPE.ACTIVITY) {
                                                    navigate(routeName.main.activityDetail.replace(":id", id))
                                                } else if (val?.content_type === CONTENT_TYPE.NEWS) {
                                                    navigate(routeName.main.pressReleaseDetail.replace(":id", id))
                                                } else {
                                                    navigate(routeName.main.contentDetail.replace(":content_no", content_no))
                                                }
                                            } else {
                                                setSelectedValue(null);
                                            }
                                        }
                                    }
                                }
                            ]}
                        />
                    </Box>
                    <Box className="flex justify-center items-center w-full max-w-fit">
                        <ChangeLanguage />
                    </Box>
                </Box>
            </Box>
            <Divider />
        </Box>
    )
}

export default HeaderWebsite